import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Input, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import { DEFAULT_VALUE } from "@/config";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import type { CustomerInfoQuery, CustomerInfoRecord } from "./CustomerInfoPage";

const memberLevelLabels: Record<number, string> = { 1: "非会员", 2: "基础会员", 3: "高级会员", 4: "企业会员" };
const memberStateLabels: Record<number, string> = { 1: "激活", 2: "过期" };
const userStateLabels: Record<number, string> = { 0: "已禁用", 1: "已启用", 2: "注销中", 3: "已注销" };

const memberLevelOptions = Object.entries(memberLevelLabels).map(([value, label]) => ({ value: Number(value), label }));
const memberStateOptions = Object.entries(memberStateLabels).map(([value, label]) => ({ value: Number(value), label }));
const contactOptions = [
    { value: true, label: "是" },
    { value: false, label: "否" }
];
const userStateOptions = [
    { value: 1, label: "已启用" },
    { value: 0, label: "已禁用" },
    { value: 2, label: "注销中" },
    { value: 3, label: "已注销" }
];

const formatDateTime = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
const formatDateOnly = (value?: string) => (value ? value.slice(0, 10) : DEFAULT_VALUE);

/** 将旧 Vue ProTable 的会员等级枚举转换为中文，未知值保持占位符。 */
function formatMemberLevel(value?: number | string): string {
    return memberLevelLabels[Number(value)] || DEFAULT_VALUE;
}

/** 将旧 Vue ProTable 的用户状态枚举转换为中文，未知值保持占位符。 */
function formatUserState(value?: number | string): string {
    return userStateLabels[Number(value)] || DEFAULT_VALUE;
}

export type CustomerSelectedQuery = Omit<CustomerInfoQuery, "page" | "size"> & {
    page?: number;
    size?: number;
    selectAll?: boolean;
    selectedIds?: number[];
    excludeIds?: number[];
};

export interface CustomerSelectedDialogProps {
    open: boolean;
    query: CustomerSelectedQuery;
    selectCount: number;
    communicateCount: number;
    loadCustomers: (query: CustomerSelectedQuery) => Promise<{ list?: CustomerInfoRecord[]; total?: number }>;
    onNavigate?: (id: number) => void;
    onCancel: () => void;
}

function getSelectedColumns(onNavigate?: (id: number) => void): TableColumnProps<CustomerInfoRecord>[] {
    return [
        {
            key: "id",
            label: "用户 ID",
            width: 180,
            prop: "customer",
            render: ({ row }) => (
                <Button type="link" onClick={() => onNavigate?.(row.customer.id)}>
                    {row.customer.code || row.customer.id}
                </Button>
            )
        },
        {
            key: "name",
            label: "个人实名/机构名称",
            minWidth: 160,
            prop: "customer",
            render: ({ row }) =>
                row.institutionApply?.institutionName ||
                row.customer.institutionName ||
                row.customer.realName ||
                DEFAULT_VALUE
        },
        {
            key: "mobile",
            label: "注册手机号",
            width: 140,
            prop: "customer",
            render: ({ row }) => row.customer.mobile || DEFAULT_VALUE
        },
        {
            key: "createdTime",
            label: "注册时间",
            width: 150,
            prop: "customer",
            render: ({ row }) => formatDateTime(row.customer.createdTime)
        },
        {
            key: "memberLevel",
            label: "会员等级",
            width: 150,
            prop: "customer",
            render: ({ row }) => formatMemberLevel(row.customer.memberLevel)
        },
        {
            key: "memberActiveTime",
            label: "会员激活时间",
            width: 150,
            prop: "customer",
            render: ({ row }) => formatDateTime(row.customer.memberActiveTime)
        },
        {
            key: "memberExpiryDate",
            label: "会员到期日期",
            width: 150,
            prop: "customer",
            render: ({ row }) => formatDateOnly(row.customer.memberExpiryDate)
        },
        {
            key: "memberState",
            label: "会员状态",
            width: 100,
            prop: "customer",
            render: ({ row }) => memberStateLabels[Number(row.customer.memberState)] || DEFAULT_VALUE
        },
        {
            key: "isConcat",
            label: "是否电联",
            width: 100,
            prop: "isConcat",
            render: ({ row }) => (row.isConcat ? "是" : "否")
        },
        {
            key: "summary",
            label: "最新沟通总结",
            width: 190,
            prop: "latestCommunicateSummary",
            render: ({ row }) => row.latestCommunicateSummary || DEFAULT_VALUE
        },
        {
            key: "state",
            label: "用户状态",
            width: 100,
            prop: "customer",
            render: ({ row }) => formatUserState(row.customer.state)
        }
    ];
}

/** 旧 Vue `viewSelectDataDialog` 的 React 迁移版，保留选中查询参数和无底部按钮配置。 */
export function CustomerSelectedDialog({
    open,
    query,
    selectCount,
    communicateCount,
    loadCustomers,
    onNavigate,
    onCancel
}: CustomerSelectedDialogProps) {
    const navigate = useNavigate();
    const [rows, setRows] = useState<CustomerInfoRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [errorMessage, setErrorMessage] = useState("");
    const [param, setParam] = useState("");
    const [createdDate, setCreatedDate] = useState<[string, string] | null>(null);
    const [memberActiveDate, setMemberActiveDate] = useState<[string, string] | null>(null);
    const [memberExpiryDate, setMemberExpiryDate] = useState<[string, string] | null>(null);
    const [memberLevel, setMemberLevel] = useState<number[]>([]);
    const [memberState, setMemberState] = useState<number | undefined>();
    const [isContact, setIsContact] = useState<boolean | undefined>();
    const [state, setState] = useState<number[]>([]);
    const [appliedFilters, setAppliedFilters] = useState<Partial<CustomerInfoQuery>>({});

    /** Vue 弹窗中的用户 ID 始终进入用户详情；保留回调供上层测试和路由集成覆盖。 */
    const goToCustomer = (id: number) => {
        if (onNavigate) {
            onNavigate(id);
            return;
        }
        navigate(`/lianYunUser/userInfo?userId=${id}`);
    };

    useEffect(() => {
        if (!open) return;
        let disposed = false;
        void loadCustomers({ ...query, ...appliedFilters, page, size: 10 })
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("已选用户加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [appliedFilters, loadCustomers, open, page, query]);

    /** 复用 Vue ProTable 的筛选字段映射，并只在点击搜索后更新查询条件。 */
    const submitSearch = () => {
        const filters: Partial<CustomerInfoQuery> = {};
        if (param.trim()) filters.param = param.trim();
        if (memberLevel.length) filters.memberLevel = memberLevel;
        if (memberState != null) filters.memberState = memberState;
        if (isContact != null) filters.isContact = isContact;
        if (state.length) filters.state = state;
        if (createdDate?.[0] && createdDate[1]) {
            filters.registeredBeginTime = `${createdDate[0]}T00:00:00`;
            filters.registeredEndTime = `${createdDate[1]}T23:59:59`;
        }
        if (memberActiveDate?.[0] && memberActiveDate[1]) {
            filters.activeBeginTime = `${memberActiveDate[0]}T00:00:00`;
            filters.activeEndTime = `${memberActiveDate[1]}T23:59:59`;
        }
        if (memberExpiryDate?.[0] && memberExpiryDate[1]) {
            filters.expiryBeginDate = memberExpiryDate[0];
            filters.expiryEndDate = memberExpiryDate[1];
        }
        setAppliedFilters(filters);
        setPage(1);
    };

    /** 清空筛选草稿和已提交条件，恢复父列表的选中查询范围。 */
    const resetSearch = () => {
        setParam("");
        setCreatedDate(null);
        setMemberActiveDate(null);
        setMemberExpiryDate(null);
        setMemberLevel([]);
        setMemberState(undefined);
        setIsContact(undefined);
        setState([]);
        setAppliedFilters({});
        setPage(1);
    };

    const columns = getSelectedColumns(goToCustomer);

    return (
        <ZhDialog
            className="customer-selected-dialog"
            open={open}
            alignCenter
            title="查看已选"
            width={1200}
            footer={null}
            onCancel={onCancel}
            destroyOnHidden
        >
            <ZhTable<CustomerInfoRecord>
                data={rows}
                columns={columns}
                rowKey={row => String(row.customer.id)}
                pagination={false}
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        <div className="customer-selected-dialog__filters">
                            <Input
                                aria-label="筛选已选用户"
                                placeholder="请输入用户ID、手机号、个人实名、机构名称"
                                value={param}
                                onChange={event => setParam(event.target.value)}
                            />
                            <ZhDateRangePicker
                                clearable
                                endPlaceholder="注册结束时间"
                                format="YYYY-MM-DD"
                                rangeSeparator="-"
                                startPlaceholder="注册开始时间"
                                type="daterange"
                                value={createdDate || ["", ""]}
                                valueFormat="YYYY-MM-DD"
                                onChange={(_value, formattedValue) =>
                                    setCreatedDate(formattedValue as [string, string] | null)
                                }
                            />
                            <Select
                                aria-label="筛选会员等级"
                                maxTagCount={1}
                                mode="multiple"
                                options={memberLevelOptions}
                                placeholder="请选择会员等级"
                                value={memberLevel}
                                onChange={values => setMemberLevel(values.map(Number))}
                            />
                            <ZhDateRangePicker
                                clearable
                                endPlaceholder="激活结束时间"
                                format="YYYY-MM-DD"
                                rangeSeparator="-"
                                startPlaceholder="激活开始时间"
                                type="daterange"
                                value={memberActiveDate || ["", ""]}
                                valueFormat="YYYY-MM-DD"
                                onChange={(_value, formattedValue) =>
                                    setMemberActiveDate(formattedValue as [string, string] | null)
                                }
                            />
                            <span aria-hidden="true" className="customer-selected-dialog__filter-break" />
                            <ZhDateRangePicker
                                clearable
                                endPlaceholder="到期结束日期"
                                format="YYYY-MM-DD"
                                rangeSeparator="-"
                                startPlaceholder="到期开始日期"
                                type="daterange"
                                value={memberExpiryDate || ["", ""]}
                                valueFormat="YYYY-MM-DD"
                                onChange={(_value, formattedValue) =>
                                    setMemberExpiryDate(formattedValue as [string, string] | null)
                                }
                            />
                            <Select
                                aria-label="筛选会员状态"
                                options={memberStateOptions}
                                placeholder="请选择会员状态"
                                value={memberState}
                                onChange={value => setMemberState(value == null ? undefined : Number(value))}
                            />
                            <Select
                                aria-label="筛选是否电联"
                                options={contactOptions}
                                placeholder="请选择是否电联"
                                value={isContact}
                                onChange={value => setIsContact(value == null ? undefined : Boolean(value))}
                            />
                            <Select
                                aria-label="筛选用户状态"
                                className="customer-selected-dialog__user-state"
                                maxTagCount={1}
                                mode="multiple"
                                options={userStateOptions}
                                placeholder="请选择状态"
                                value={state}
                                onChange={values => setState(values.map(Number))}
                            />
                            <Button
                                aria-label="查询已选用户 搜索"
                                icon={<TableSearchIcon name="search" />}
                                type="primary"
                                onClick={submitSearch}
                            >
                                搜索
                            </Button>
                            <Button
                                aria-label="重置已选用户筛选"
                                className="customer-selected-dialog__reset"
                                icon={<TableSearchIcon name="reset" />}
                                onClick={resetSearch}
                            />
                        </div>
                        <Space className="customer-selected-dialog__summary mb-4" wrap>
                            <Typography.Text>选中用户数：{selectCount}条</Typography.Text>
                            <Typography.Text>已选沟通记录数：{communicateCount}条</Typography.Text>
                        </Space>

                        {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                    </div>
                }
                append={
                    <div className="admin-table-composed-pagination">
                        <ZhPagination current={page} pageSize={10} total={total} hideOnSinglePage onChange={setPage} />
                    </div>
                }
            />
        </ZhDialog>
    );
}

export default CustomerSelectedDialog;
